import React, { FC, PropsWithChildren, useContext } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AuthContext } from '../contexts/AuthContext';
import { getCurrentCompanies } from 'src/modules/companies/slices';
import { getApplicationSettings } from 'src/modules/applications/slices';
import { Typography } from '@mui/material';
import { Box } from '@mui/material';
import { handleLogout } from '../helpers/helpers';

interface PrivateRouteProps {}

const PrivateRoute: FC<PropsWithChildren<PrivateRouteProps>> = ({ children }) => {
  const authContext = useContext(AuthContext);
  const dispatch = useDispatch();
  const companies = useSelector(getCurrentCompanies);
  const applicationSettings = useSelector(getApplicationSettings);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  if (typeof authContext?.isAuthenticated !== 'boolean') return null;

  if (!authContext.isAuthenticated) {
    // Pass the current URL as state for redirect after login
    const currentUrl = window.location.pathname + window.location.search;
    return <Navigate to="/login" state={{ from: currentUrl }} replace />;
  }

  /*
    Below the redirect above, which a signed-out client is owed at once, and above the companies: startup is
    what puts those in the store and it runs after the login that reports the client as authenticated. Signup
    signs a client in and sends them to their funnel step, where the empty store read as an account with no
    companies and answered the new client with a 404.
  */
  if (!authContext.isReady) return null;

  const companyId = searchParams.get('companyId');

  const hasNoCompanies = companies === null || companies === undefined || companies.length === 0;
  const hasInvalidCompanyId = !hasNoCompanies && companyId && !companies?.some(c => c.id === companyId);

  if (hasNoCompanies || hasInvalidCompanyId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" color="error" gutterBottom>
          404 - Page Not Found
        </Typography>

        {hasNoCompanies ? (
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            You do not currently have access to any active companies within{' '}
            {applicationSettings?.name ? <b>{applicationSettings.name}</b> : 'the application'}.
            <br />
            If you believe this is in error, please reach out to{' '}
            {applicationSettings?.email ? (
              <a
                href={`mailto:${applicationSettings.email}`}
              style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                {applicationSettings.email}
              </a>
            ) : (
              <span>support</span>
            )}
            .
          </Typography>
        ) : (
          <>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              The requested company could not be found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The URL you&apos;re using refers to an invalid company ID.
            </Typography>
          </>
        )}

        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: 'pointer', textDecoration: 'underline', padding: 4 }}
          onClick={() => {
            if (hasNoCompanies) {
              handleLogout(authContext, dispatch);
              navigate('/login');
            } else {
              navigate('/home');
            }
          }}
        >
          {hasNoCompanies ? 'Log Out' : 'Return to Home'}
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default PrivateRoute;
